import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Radar, PhoneCall, Send, Sparkles } from "lucide-react";

const AGENTS = [
  { name: "Scout", role: "Finds & watches", icon: Radar, to: "/ai-revenue-center/prospecting", color: "#2563EB" },
  { name: "Ava", role: "Answers & qualifies", icon: PhoneCall, to: "/ai-revenue-center/receptionist", color: "#0EA5E9" },
  { name: "Relay", role: "Follows up", icon: Send, to: "/ai-revenue-center/outreach", color: "#7C3AED" },
];

const STEPS = ["Attract", "Answer", "Qualify", "Book", "Follow up", "Measure"];

export default function RevenueCenterTeaser() {
  return (
    <section className="py-20 bg-slate-900 relative overflow-hidden" data-testid="home-revenue-teaser">
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full blur-3xl bg-brand/20" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-300">
              <Sparkles size={13} /> New · AI Revenue Center™
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mt-4">
              From website visitor to <span className="text-blue-400">qualified customer — automatically.</span>
            </h2>
            <p className="text-slate-300 mt-4 text-lg">
              Your tools, finally working as one system. WebsiteTech answers the inquiry, qualifies the opportunity, books the appointment, follows up on approved channels, updates your CRM — and shows what produced revenue.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-6">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-200 bg-white/10 border border-white/10 rounded-full px-3.5 py-1.5">{s}</span>
                  {i < STEPS.length - 1 && <ArrowRight size={13} className="text-slate-500" />}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/ai-revenue-center" data-testid="home-revenue-explore" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-brand text-white hover:-translate-y-0.5 transition-transform">
                Explore the Revenue Center <ArrowRight size={16} />
              </Link>
              <Link to="/ai-revenue-center/packages" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 border border-white/25 text-white hover:bg-white/10 transition-colors">
                See packages
              </Link>
            </div>
          </motion.div>

          <div className="grid gap-4">
            {AGENTS.map((a, i) => (
              <motion.div key={a.name} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Link to={a.to} data-testid={`home-revenue-agent-${a.name.toLowerCase()}`}
                  className="group flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 transition-colors">
                  <div className="h-12 w-12 rounded-2xl grid place-items-center text-white shrink-0" style={{ background: a.color }}><a.icon size={22} /></div>
                  <div className="min-w-0">
                    <div className="font-heading text-lg font-bold text-white">{a.name}</div>
                    <div className="text-sm text-slate-400">{a.role}</div>
                  </div>
                  <ArrowRight size={18} className="ml-auto text-slate-500 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
